// Comportamientos compartidos del tema FIKA. Sin dependencias.

// Revela un elemento la primera vez que entra a la vista (el movimiento vive en CSS, clase .reveal).
function initReveal(root = document) {
  const items = root.querySelectorAll('.reveal:not([data-visible])');
  if (!('IntersectionObserver' in window)) {
    items.forEach((el) => el.setAttribute('data-visible', ''));
    return;
  }
  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          entry.target.setAttribute('data-visible', '');
          observer.unobserve(entry.target);
        }
      }
    },
    { rootMargin: '0px 0px -80px 0px' },
  );
  items.forEach((el) => observer.observe(el));
}

// Hojas y diálogos: [data-open-dialog="id"] abre, [data-close-dialog] cierra, clic en el fondo cierra.
function initDialogs() {
  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-open-dialog]');
    if (opener) {
      document.getElementById(opener.dataset.openDialog)?.showModal();
      return;
    }
    const closer = event.target.closest('[data-close-dialog]');
    if (closer) {
      closer.closest('dialog')?.close();
      return;
    }
    if (event.target instanceof HTMLDialogElement) {
      event.target.close();
    }
  });
}

// Videos de creadores: al tocar la portada se muestra el video; solo uno suena a la vez.
function initVideos() {
  document.addEventListener('click', (event) => {
    const play = event.target.closest('[data-video-play]');
    if (!play) return;
    const card = play.closest('[data-video-card]');
    const video = card?.querySelector('video');
    if (!video) return;
    document.querySelectorAll('[data-video-card] video').forEach((other) => {
      if (other !== video) other.pause();
    });
    card.querySelector('[data-video-poster]').hidden = true;
    video.classList.remove('hidden');
    video.play();
  });
}

// Galería de producto: carrusel con scroll-snap. Las miniaturas llevan a su vista y se marca la que está a la vista.
function initGallery(root = document) {
  root.querySelectorAll('[data-gallery]').forEach((gallery) => {
    const track = gallery.querySelector('[data-gallery-track]');
    const slides = [...gallery.querySelectorAll('[data-gallery-slide]')];
    const thumbs = [...gallery.querySelectorAll('[data-gallery-thumb]')];
    if (!track) return;
    thumbs.forEach((thumb, index) => {
      thumb.addEventListener('click', () => track.scrollTo({ left: slides[index].offsetLeft - track.offsetLeft }));
    });
    if (!('IntersectionObserver' in window)) return;
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          const index = slides.indexOf(entry.target);
          thumbs.forEach((thumb, i) => thumb.setAttribute('aria-pressed', String(i === index)));
          // Solo se mueve la fila de miniaturas, nunca la página
          const list = gallery.querySelector('[data-gallery-thumbs]');
          const thumb = thumbs[index];
          if (list && thumb) list.scrollTo({ left: thumb.offsetLeft - list.offsetLeft - 8 });
        }
      },
      { root: track, threshold: 0.6 },
    );
    slides.forEach((slide) => observer.observe(slide));
  });
}

// Caja de compra: cantidad, total, enlace directo al pago y barra fija en celular.
function initPurchase(root = document) {
  root.querySelectorAll('[data-purchase]').forEach((box) => {
    const { price, compare, currency, portions, cartUrl, variant, singular, plural } = box.dataset;
    const max = Number(box.dataset.max || 6);
    const format = (cents) =>
      '$' + (cents / 100).toLocaleString('es-MX', { minimumFractionDigits: cents % 100 ? 2 : 0, maximumFractionDigits: 2 }) + ' ' + currency;
    const minus = box.querySelector('[data-qty-minus]');
    const plus = box.querySelector('[data-qty-plus]');
    let quantity = 1;

    const paint = () => {
      box.querySelector('[data-qty-value]').textContent = quantity;
      const input = box.querySelector('[data-qty-input]');
      if (input) input.value = quantity;
      box.querySelectorAll('[data-purchase-total]').forEach((el) => (el.textContent = format(price * quantity)));
      const compareEl = box.querySelector('[data-purchase-compare]');
      if (compareEl) compareEl.textContent = format(compare * quantity);
      const template = quantity === 1 ? singular : plural;
      box.querySelector('[data-purchase-note]').textContent = template
        .replace('[bolsas]', quantity)
        .replace('[porciones]', portions * quantity);
      box.querySelectorAll('[data-purchase-checkout]').forEach((link) => (link.href = `${cartUrl}/${variant}:${quantity}`));
      minus.disabled = quantity <= 1;
      plus.disabled = quantity >= max;
    };
    minus?.addEventListener('click', () => { quantity = Math.max(1, quantity - 1); paint(); });
    plus?.addEventListener('click', () => { quantity = Math.min(max, quantity + 1); paint(); });

    const bar = box.querySelector('[data-buy-bar]');
    const actions = box.querySelector('[data-purchase-actions]');
    if (bar && actions && 'IntersectionObserver' in window) {
      new IntersectionObserver(([entry]) => {
        const visible = !entry.isIntersecting;
        bar.toggleAttribute('data-visible', visible);
        bar.setAttribute('aria-hidden', String(!visible));
        const control = bar.querySelector('a, button');
        if (control) control.tabIndex = visible ? 0 : -1;
      }).observe(actions);
    }
  });
}

// Carrito lateral: al agregar un producto se abre solo, con el botón de pago a la vista.
// Si algo falla, el formulario se envía normal y Shopify lleva a la página del carrito.
function initCartDrawer() {
  const drawer = document.querySelector('[data-cart-drawer]');
  if (!drawer) return;
  const { cartUrl, cartAddUrl, cartChangeUrl } = drawer.dataset;

  const refresh = async () => {
    const response = await fetch(`${cartUrl}?section_id=cart-drawer`);
    const html = await response.text();
    const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-cart-drawer-content]');
    if (!fresh) throw new Error('cart-drawer');
    drawer.querySelector('[data-cart-drawer-content]').replaceWith(fresh);
    document.querySelectorAll('[data-cart-count]').forEach((el) => {
      el.textContent = fresh.dataset.count;
      el.closest('[data-cart-pill]')?.toggleAttribute('hidden', fresh.dataset.count === '0');
    });
  };

  document.addEventListener('submit', async (event) => {
    const form = event.target.closest('form[data-cart-form]');
    if (!form) return;
    event.preventDefault();
    const button = form.querySelector('[type=submit]');
    button?.setAttribute('disabled', '');
    try {
      const response = await fetch(`${cartAddUrl}.js`, { method: 'POST', body: new FormData(form), headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error('add');
      await refresh();
      drawer.showModal();
    } catch {
      form.removeAttribute('data-cart-form');
      form.submit();
    } finally {
      button?.removeAttribute('disabled');
    }
  });

  drawer.addEventListener('click', async (event) => {
    const change = event.target.closest('[data-cart-change]');
    if (!change) return;
    drawer.setAttribute('aria-busy', 'true');
    try {
      await fetch(`${cartChangeUrl}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line: Number(change.dataset.line), quantity: Number(change.dataset.quantity) }),
      });
      await refresh();
    } finally {
      drawer.removeAttribute('aria-busy');
    }
  });

  // La barra fija del celular usa el mismo formulario
  document.addEventListener('click', (event) => {
    if (event.target.closest('[data-buy-bar-add]')) {
      document.querySelector('form[data-cart-form]')?.requestSubmit();
    }
  });
}

// Problema: una pestaña por caso; se muestra un caso a la vez.
function initCases(root = document) {
  root.querySelectorAll('[data-cases]').forEach((group) => {
    const tabs = [...group.querySelectorAll('[data-case-tab]')];
    const panels = [...group.querySelectorAll('[data-case-panel]')];
    if (tabs.length < 2) return;
    const show = (index) => {
      panels.forEach((panel, i) => (panel.hidden = i !== index));
      tabs.forEach((tab, i) => tab.setAttribute('aria-pressed', String(i === index)));
    };
    show(0);
    group.setAttribute('data-cases-ready', '');
    tabs.forEach((tab, index) => tab.addEventListener('click', () => show(index)));
  });
}

initReveal();
initDialogs();
initCases();
initVideos();
initGallery();
initPurchase();
initCartDrawer();

// El editor de temas vuelve a pintar secciones sin recargar la página.
document.addEventListener('shopify:section:load', (event) => {
  initReveal(event.target);
  initCases(event.target);
  initGallery(event.target);
  initPurchase(event.target);
});
